import {
  Conversation,
  CosmosDBHealth,
  ChatHistoryLoadingState,
} from '../api/models';

export interface AppState {
  chatHistory: Conversation[];
  currentConversation: Conversation | null;
  isCosmosDBAvailable: CosmosDBHealth;
  chatHistoryLoadingState: ChatHistoryLoadingState;
  isChatHistoryOpen: boolean;
  frontendSettings: any;
  isLoading: boolean;
}

export type AppAction =
  | { type: 'FETCH_CHAT_HISTORY_INIT' }
  | { type: 'FETCH_CHAT_HISTORY_SUCCESS'; payload: Conversation[] }
  | { type: 'FETCH_CHAT_HISTORY_ERROR' }
  | { type: 'SET_CURRENT_CONVERSATION'; payload: Conversation | null }
  | { type: 'DELETE_CHAT_ENTRY'; payload: string }
  | { type: 'DELETE_CHAT_HISTORY' }
  | { type: 'TOGGLE_CHAT_HISTORY' }
  | { type: 'UPDATE_CONVERSATION_TITLE'; payload: { id: string; title: string } }
  | { type: 'SET_COSMOSDB_STATUS'; payload: CosmosDBHealth }
  | { type: 'SET_FRONTEND_SETTINGS'; payload: any }
  | { type: 'ADD_CONVERSATION'; payload: Conversation }
  | { type: 'UPDATE_CONVERSATION'; payload: Conversation };

export const initialState: AppState = {
  chatHistory: [],
  currentConversation: null,
  isCosmosDBAvailable: {
    cosmosDB: false,
    status: 'Not configured',
  },
  chatHistoryLoadingState: ChatHistoryLoadingState.NotStarted,
  isChatHistoryOpen: false,
  frontendSettings: null,
  isLoading: false,
};

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'FETCH_CHAT_HISTORY_INIT':
      return {
        ...state,
        chatHistoryLoadingState: ChatHistoryLoadingState.Loading,
      };

    case 'FETCH_CHAT_HISTORY_SUCCESS':
      return {
        ...state,
        chatHistory: action.payload,
        chatHistoryLoadingState: ChatHistoryLoadingState.Success,
      };

    case 'FETCH_CHAT_HISTORY_ERROR':
      return {
        ...state,
        chatHistoryLoadingState: ChatHistoryLoadingState.Fail,
      };

    case 'SET_CURRENT_CONVERSATION':
      return {
        ...state,
        currentConversation: action.payload,
      };

    case 'DELETE_CHAT_ENTRY': {
      const updatedHistory = state.chatHistory.filter(
        conv => conv.id !== action.payload,
      );
      return {
        ...state,
        chatHistory: updatedHistory,
        currentConversation:
          state.currentConversation?.id === action.payload
            ? null
            : state.currentConversation,
      };
    }

    case 'DELETE_CHAT_HISTORY':
      return {
        ...state,
        chatHistory: [],
        currentConversation: null,
      };

    case 'TOGGLE_CHAT_HISTORY':
      return {
        ...state,
        isChatHistoryOpen: !state.isChatHistoryOpen,
      };

    case 'UPDATE_CONVERSATION_TITLE': {
      const updatedHistory = state.chatHistory.map(conv =>
        conv.id === action.payload.id
          ? { ...conv, title: action.payload.title }
          : conv,
      );
      return {
        ...state,
        chatHistory: updatedHistory,
        currentConversation:
          state.currentConversation?.id === action.payload.id
            ? { ...state.currentConversation, title: action.payload.title }
            : state.currentConversation,
      };
    }

    case 'SET_COSMOSDB_STATUS':
      return {
        ...state,
        isCosmosDBAvailable: action.payload,
      };

    case 'SET_FRONTEND_SETTINGS':
      return {
        ...state,
        frontendSettings: action.payload,
      };

    case 'ADD_CONVERSATION':
      return {
        ...state,
        chatHistory: [action.payload, ...state.chatHistory],
      };

    case 'UPDATE_CONVERSATION': {
      const updatedHistory = state.chatHistory.map(conv =>
        conv.id === action.payload.id ? action.payload : conv,
      );
      return {
        ...state,
        chatHistory: updatedHistory,
      };
    }

    default:
      return state;
  }
}

